import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form } from 'react-bootstrap';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import InputItem from '../Forms/InputItem/InputItem';
import FormButton from '../Forms/FormButton/FormButton';
import CustomSelect from '../Forms/CustomSelect/CustomSelect';
import Fader from '../Loaders/Fader';

import { USER_ROLES } from '../../../util/constants/AppConstants';
import { setNotify } from '../../../datastore/actions/actionActions';
import {
  assignCity,
  assignMpp,
  createAdmin,
  createUser,
  deleteAdmin,
  deleteCity,
  deleteEmailSettings,
  deleteMpp,
  getMpps,
  saveUser,
  sendVerificationCode,
  updateAdmin,
  updateCity,
  updateMpp,
  verifyCode,
} from '../../../http/usersAPI';

import { assignRiding, deleteRiding, getAllRidings, updateRiding } from '../../../http/ridingAPI';
import { 
  assignFederalDistrict, 
  deleteFederalDistrict, 
  getAllFederalDistricts, 
  updateFederalDistrict 
} from '../../../http/federalDistrictsAPI';
import { getAllCities } from '../../../http/citiesAPI';

import useOutsideClick from '../../../util/useOutsideClick';

import UserIcon from '../../../assets/images/icons/user.svg';
import PasswordIcon from '../../../assets/images/icons/password.svg';
import EngagementIcon from '../../../assets/images/icons/engagement.svg';
import JobIcon from '../../../assets/images/icons/job.svg';
import EmailIcon from '../../../assets/images/icons/email.svg';
import CityIcon from '../../../assets/images/icons/city.svg';
import StateIcon from '../../../assets/images/icons/state.svg';
import CountryIcon from '../../../assets/images/icons/globe.svg';
import UserRoleIcon from '../../../assets/images/icons/role.svg';
import TopicIcon from '../../../assets/images/icons/topic.svg';
import './UserAdd.scss';

const UserAdd = ({ isOpen, onSaveHandler, onCloseHandler, userData }) => {
  const dispatch = useDispatch();

  const [usersList, setUsersList] = useState([]);
  const [ridings, setRidings] = useState([]);
  const [federalDistricts, setFederalDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [jobTitle, setJobTitle] = useState('');
  const [city, setCity] = useState('');
  const [locationState, setLocationState] = useState('');
  const [country, setCountry] = useState('');
  const [userRole, setUserRole] = useState('');
  const [demoUser, setDemoUser] = useState(false);
  const [bypass2fa, setBypass2fa] = useState(false);
  const [password, setPassword] = useState('');
  const [usersLimit, setUsersLimit] = useState(2);
  const [topicsPermission, setTopicsPermission] = useState(true);
  const [groupsPermission, setGroupsPermission] = useState(true);
  const [trendsPermission, setTrendsPermission] = useState(true);
  const [districtPermission, setDistrictPermission] = useState(true);
  const [mpPermission, setMpPermission] = useState(true);
  const [mpp, setMpp] = useState(null);
  const [riding, setRiding] = useState(null);
  const [federalDistrict, setFederalDistrict] = useState(null);
  const [councillorCity, setCouncillorCity] = useState(null);
  const [saving, setSaving] = useState(false);

  const config = useSelector((state) => state.config.config);

  const windowRef = useRef();

  const adminRoleValue = USER_ROLES.find((role) => role.short === 'Admin').value;
  const stafferRoleValue = USER_ROLES.find((role) => role.short === 'Constituent Staff').value;
  const managerRoleValue = USER_ROLES.find((role) => role.short === 'Manager').value;
  const mppRoleValue = USER_ROLES.find((role) => role.short === 'MPP').value;
  const mpRoleValue = USER_ROLES.find((role) => role.short === 'MP').value;
  const councillorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;

  const handleGetMpps = async () => {
    const res = await getMpps(config);

    if (res.status === 200) {
      setUsersList(
        res.data.users
          .filter((user) => !userData || user.id !== userData.id)
          .map((user) => ({
            value: user.id,
            label: `${user.firstname} ${user.lastname}`,
          })),
      );
    } else {
      console.log('ERROR: Grabbing MPPs');
    }
  };

  const handleGetRidings = async () => {
    const res = await getAllRidings(config);

    if (res.status === 200) {
      setRidings(
        res.data.ridings.map((riding) => ({
          value: riding.id,
          label: riding.name,
        })),
      );
    } else {
      console.log('ERROR: Grabbing Ridings');
    }
  };

  const handleGetFederalDistricts = async () => {
    const res = await getAllFederalDistricts(config);

    if (res.status === 200) {
      setFederalDistricts(
        res.data.federal_districts.map((district) => ({
          value: district.id,
          label: district.name,
        })),
      );
    } else {
      console.log('ERROR: Grabbing Federal Districts', res);
    }
  };

  const handleGetCities = async () => {
    const res = await getAllCities(config);

    if (res.status === 200) {
      setCities(
        res.data.cities.map((city) => ({
          value: city.id,
          label: city.name,
        })),
      );
    } else {
      console.log('ERROR: Grabbing Cities');
    }
  };

  useEffect(() => {
    handleGetMpps();
    handleGetRidings();
    handleGetFederalDistricts();
    handleGetCities();

    // Auto-verify phone in dev environment
    if (config.env === 'dev') {
      setIsPhoneVerified(true);
    }
  }, []);

  useEffect(() => {
    if (userData) {
      setFirstname(userData.firstname);
      setLastname(userData.lastname);
      setEmail(userData.email);
      setPhone(`+${userData.phone}`);
      setVerifiedPhone(`+${userData.phone}`);
      setIsPhoneVerified(true);
      setJobTitle(userData.job_title);
      setLocationState(userData.location_state);
      setCity(userData.location_city);
      setCountry(userData.location_country);
      setUserRole(userData.role);
      setDemoUser(userData.demo);
      setBypass2fa(userData.bypass2fa || false);

      if (userData.role === adminRoleValue) {
        setUsersLimit(userData.permissions.users_limit);
        setTopicsPermission(userData.permissions.topics_permission);
        setGroupsPermission(userData.permissions.groups_permission);
        setTrendsPermission(userData.permissions.trends_permission);
        setDistrictPermission(userData.permissions.district_permission);
        setMpPermission(userData.permissions.mp_permission);
      }

      if (userData.role === stafferRoleValue || userData.role === managerRoleValue) {
        setMpp(userData.mpp.id);
      }

      if (userData.role === mppRoleValue) {
        setRiding(userData.riding.id);
      }

      if (userData.role === mpRoleValue) {
        setFederalDistrict(userData.federal_district?.id ?? null);
      }

      if (userData.role === councillorRoleValue) {
        setCouncillorCity(userData.city.id);
      }
    }
  }, [userData]);

  const handlePhoneChange = (e) => {
    let { value } = e.target;

    const digits = value.replace(/\D/g, '');

    if (digits.length === 0) {
      value = '';
    } else {
      value = `+${digits}`;
    }

    setPhone(value);

    if (config.env === 'dev' && value) {
      setIsPhoneVerified(true);
      setVerifiedPhone(value);
      setCodeSent(true);
    } else if (value !== verifiedPhone) {
      setIsPhoneVerified(false);
      setCodeSent(false);
    } else if (verifiedPhone && value === verifiedPhone) {
      setIsPhoneVerified(true);
      setCodeSent(true);
    }
  };

  const handleSendCode = async () => {
    if (!phone) {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Please enter a phone number',
        }),
      );
      return;
    }

    const parsed = parsePhoneNumberFromString(phone);
    if (!parsed || !parsed.isValid()) {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Please enter a valid phone number',
        }),
      );
      return;
    }

    const res = await sendVerificationCode(config, phone);
    if (res.status === 200 && res.data.success) {
      setCodeSent(true);
      setVerificationCode('');
      dispatch(
        setNotify({
          status: true,
          type: 'success',
          title: 'Success',
          message: 'Verification code has been sent',
        }),
      );
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Verification code could not be sent. Please try again later',
        }),
      );
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Please enter the verification code',
        }),
      );
      return;
    }

    if (verificationCode.trim().length !== 6) {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Verification code must be 6 digits long',
        }),
      );
      return;
    }

    const res = await verifyCode(config, phone, verificationCode);
    if (res.status === 200) {
      if (res.data.success) {
        setIsPhoneVerified(true);
        setVerificationCode('');
        setCodeSent(false);
        setVerifiedPhone(phone);
        dispatch(setNotify({ status: true, type: 'success', title: 'Success', message: 'Phone verified!' }));
      } else {
        dispatch(setNotify({ status: true, type: 'error', title: 'Error', message: res.data.message }));
      }
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Verification code could not be verified. Please try again later',
        }),
      );
    }
  };

  const checkIsValid = () => {
    if (
      firstname &&
      lastname &&
      email &&
      phone &&
      jobTitle &&
      city &&
      locationState &&
      country &&
      userRole &&
      (userRole !== stafferRoleValue || (userRole === stafferRoleValue && mpp)) &&
      (userRole !== managerRoleValue || (userRole === managerRoleValue && mpp)) &&
      (userRole !== mppRoleValue || (userRole === mppRoleValue && riding)) &&
      (userRole !== mpRoleValue || (userRole === mpRoleValue && federalDistrict)) &&
      (userRole !== councillorRoleValue || (userRole === councillorRoleValue && councillorCity))
    ) {
      return true;
    }

    return false;
  };

  const handleSuccess = (message) => {
    dispatch(
      setNotify({
        status: true,
        type: 'success',
        title: 'Success',
        message,
      }),
    );

    onSaveHandler();
  };

  const handleSave = async () => {
    if (!checkIsValid()) {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Fill in all required fields',
        }),
      );
      return;
    }

    if (!isPhoneVerified && config.env !== 'dev') {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Phone is not verified',
        }),
      );
      return;
    }

    try {
      setSaving(true);

      const res = await saveUser(
        config,
        userData.id,
        firstname,
        lastname,
        email.toLowerCase(),
        phone.replace(/\D/g, ''),
        jobTitle,
        city,
        locationState,
        country,
        userRole,
        password,
        userRole === stafferRoleValue ||
          userRole === managerRoleValue ||
          userRole === mppRoleValue ||
          userRole === mpRoleValue ||
          userRole === councillorRoleValue
          ? demoUser
          : false,
        bypass2fa,
      );

      if (res.status !== 'success') {
        throw new Error(res.message);
      }

      if (userRole === adminRoleValue && userData.role === adminRoleValue) {
        const res2 = await updateAdmin(
          config,
          userData.id,
          usersLimit,
          topicsPermission,
          groupsPermission,
          trendsPermission,
          districtPermission,
          mpPermission,
        );
        if (res2.status !== 200) {
          throw new Error('ERROR: Updating Admin Permissions');
        }
      }

      if (userRole === adminRoleValue && userData.role !== adminRoleValue) {
        const res2 = await createAdmin(
          config,
          userData.id,
          usersLimit,
          topicsPermission,
          groupsPermission,
          trendsPermission,
          districtPermission,
          mpPermission,
        );
        if (res2.status !== 201) {
          throw new Error('ERROR: Setting Admin Permissions');
        }
      }

      if (userRole !== adminRoleValue && userData.role === adminRoleValue) {
        const res2 = await deleteAdmin(config, userData.id);
        if (res2.status !== 200) {
          throw new Error('ERROR: Deleting Admin Permissions');
        }
      }

      if (
        (userRole === stafferRoleValue && userData.role === stafferRoleValue) ||
        (userRole === managerRoleValue && userData.role === managerRoleValue)
      ) {
        const res2 = await updateMpp(config, userData.id, mpp);
        if (res2.status !== 200) {
          throw new Error('ERROR: Updating MPP for Staffer');
        }
      }

      if (
        (userRole === stafferRoleValue && userData.role !== stafferRoleValue) ||
        (userRole === managerRoleValue && userData.role !== managerRoleValue)
      ) {
        const res2 = await assignMpp(config, userData.id, mpp);
        if (res2.status !== 201) {
          throw new Error('ERROR: Setting MPP for Staffer');
        }
      }

      if (
        (userRole !== stafferRoleValue && userData.role === stafferRoleValue) ||
        (userRole !== managerRoleValue && userData.role === managerRoleValue)
      ) {
        const res2 = await deleteMpp(config, userData.id);
        if (res2.status !== 200) {
          throw new Error('ERROR: Deleting MPP for Staffer');
        }
      }

      if (userRole === mppRoleValue && userData.role === mppRoleValue) {
        const res2 = await updateRiding(config, userData.id, riding);
        if (res2.status !== 200) {
          throw new Error('ERROR: Updating Riding for MPP');
        }
      }

      if (userRole === mppRoleValue && userData.role !== mppRoleValue) {
        const res2 = await assignRiding(config, userData.id, riding);
        if (res2.status !== 201) {
          throw new Error('ERROR: Setting Riding for MPP');
        }
      }

      if (userRole !== mppRoleValue && userData.role === mppRoleValue) {
        const res2 = await deleteRiding(config, userData.id);
        if (res2.status !== 200) {
          throw new Error('ERROR: Deleting Riding for MPP');
        }

        const res3 = await deleteEmailSettings(config, userData.id);
        if (res3.status !== 200) {
          throw new Error('ERROR: Deleting Email Settings for MPP');
        }
      }

      if (userRole === mpRoleValue && userData.role === mpRoleValue) {
        const res2 = await updateFederalDistrict(config, userData.id, federalDistrict);
        if (res2.status !== 200) {
          throw new Error('ERROR: Updating Federal District for MP');
        }
      }

      if (userRole === mpRoleValue && userData.role !== mpRoleValue) {
        const res2 = await assignFederalDistrict(config, userData.id, federalDistrict);
        if (res2.status !== 201) {
          throw new Error('ERROR: Setting Federal District for MP');
        }
      }

      if (userRole !== mpRoleValue && userData.role === mpRoleValue) {
        const res2 = await deleteFederalDistrict(config, userData.id);
        if (res2.status !== 200) {
          throw new Error('ERROR: Deleting Federal District for MP');
        }

        const res3 = await deleteEmailSettings(config, userData.id);
        if (res3.status !== 200) {
          throw new Error('ERROR: Deleting Email Settings for MP');
        }
      }

      if (userRole === councillorRoleValue && userData.role === councillorRoleValue) {
        const res2 = await updateCity(config, userData.id, councillorCity);
        if (res2.status !== 200) {
          throw new Error('ERROR: Updating City for Councillor');
        }
      }

      if (userRole === councillorRoleValue && userData.role !== councillorRoleValue) {
        const res2 = await assignCity(config, userData.id, councillorCity);
        if (res2.status !== 201) {
          throw new Error('ERROR: Setting City for Councillor');
        }
      }

      if (userRole !== councillorRoleValue && userData.role === councillorRoleValue) {
        const res2 = await deleteCity(config, userData.id);
        if (res2.status !== 200) {
          throw new Error('ERROR: Deleting City for Councillor');
        }

        const res3 = await deleteEmailSettings(config, userData.id);
        if (res3.status !== 200) {
          throw new Error('ERROR: Deleting Email Settings for Councillor');
        }
      }

      handleSuccess(res.message);
    } catch (error) {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: error.message || 'Something went wrong',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!checkIsValid() || !password) {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Fill in all the fields',
        }),
      );
      return;
    }

    if (!isPhoneVerified && config.env !== 'dev') {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: 'Phone is not verified',
        }),
      );
      return;
    }

    try {
      setSaving(true);

      const res = await createUser(
        config,
        firstname,
        lastname,
        email.toLowerCase(),
        phone.replace(/\D/g, ''),
        password,
        jobTitle,
        city,
        locationState,
        country,
        userRole,
        userRole === stafferRoleValue ||
          userRole === managerRoleValue ||
          userRole === mppRoleValue ||
          userRole === mpRoleValue ||
          userRole === councillorRoleValue
          ? demoUser
          : false,
        bypass2fa,
      );

      if (res.status !== 'success') {
        throw new Error(res.message);
      }

      if (userRole === adminRoleValue) {
        const res2 = await createAdmin(
          config,
          res.data[0].id,
          usersLimit,
          topicsPermission,
          groupsPermission,
          trendsPermission,
          districtPermission,
          mpPermission,
        );

        if (res2.status !== 201) {
          throw new Error('ERROR: Setting Admin Permissions');
        }
      }

      if (userRole === stafferRoleValue || userRole === managerRoleValue) {
        const res2 = await assignMpp(config, res.data[0].id, mpp);

        if (res2.status !== 201) {
          throw new Error('ERROR: Setting MPP for Staffer');
        }
      }

      if (userRole === mppRoleValue) {
        const res2 = await assignRiding(config, res.data[0].id, riding);

        if (res2.status !== 201) {
          throw new Error('ERROR: Setting Riding for MPP');
        }
      }

      if (userRole === mpRoleValue) {
        const res2 = await assignFederalDistrict(config, res.data[0].id, federalDistrict);

        if (res2.status !== 201) {
          throw new Error('ERROR: Setting Federal District for MP');
        }
      }

      if (userRole === councillorRoleValue) {
        const res2 = await assignCity(config, res.data[0].id, councillorCity);

        if (res2.status !== 201) {
          throw new Error('ERROR: Setting City for Councillor');
        }
      }

      handleSuccess(res.message);
    } catch (error) {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: error.message || 'Something went wrong',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  useOutsideClick(windowRef, null, isOpen, onCloseHandler);

  return (
    <div className="login-wrap user-add-wrap">
      <div className="login-view">
        <div ref={windowRef} className="login-box add-user-box">
          {!saving ? (
            <div className="logo-large">{userData ? 'Edit User' : 'Create A New User'}</div>
          ) : (
            <>
              <div className="logo-large">{userData ? 'Saving User Information' : 'Creating User'}</div>
              <div className="logo-sub user-space">
                The process should take no longer than 30 seconds. Please Wait...
              </div>
            </>
          )}
          {!saving ? (
            <>
              <div className="add-user-left">
                <InputItem
                  label="First Name"
                  type="text"
                  icon={UserIcon}
                  onChange={(e) => setFirstname(e.target.value)}
                  value={firstname}
                />
                <InputItem
                  label="Last Name"
                  type="text"
                  icon={UserIcon}
                  onChange={(e) => setLastname(e.target.value)}
                  value={lastname}
                />
                <InputItem
                  label="Email"
                  type="email"
                  icon={EmailIcon}
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                />
                <InputItem
                  label="Password"
                  type="password"
                  icon={PasswordIcon}
                  onChange={(e) => setPassword(e.target.value)}
                  value={password}
                />
                <div className="phone-field">
                  <InputItem
                    label="Phone"
                    type="text"
                    icon={EngagementIcon}
                    onChange={handlePhoneChange}
                    value={phone}
                  />
                  {!isPhoneVerified && config.env !== 'dev' && (
                    <div className="edit-btn" onClick={handleSendCode}>
                      {codeSent ? 'Resend Code' : 'Send Verification Code'}
                    </div>
                  )}
                </div>
                {codeSent && !isPhoneVerified && config.env !== 'dev' && (
                  <div className="phone-field">
                    <InputItem
                      label="Verification Code"
                      type="number"
                      icon={PasswordIcon}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      value={verificationCode}
                    />
                    <div className="edit-btn" onClick={handleVerifyCode}>
                      Verify Code
                    </div>
                  </div>
                )}
              </div>
              <div className="add-user-right">
                <InputItem
                  label="Job Title"
                  type="text"
                  icon={JobIcon}
                  onChange={(e) => setJobTitle(e.target.value)}
                  value={jobTitle}
                />
                <InputItem
                  label="City"
                  type="text"
                  icon={CityIcon}
                  onChange={(e) => setCity(e.target.value)}
                  value={city}
                />
                <InputItem
                  label="Province/State"
                  type="text"
                  icon={StateIcon}
                  onChange={(e) => setLocationState(e.target.value)}
                  value={locationState}
                />
                <InputItem
                  label="Country"
                  type="text"
                  icon={CountryIcon}
                  onChange={(e) => setCountry(e.target.value)}
                  value={country}
                />
                <CustomSelect
                  options={USER_ROLES}
                  selectedOption={userRole}
                  setSelectedOption={setUserRole}
                  defaultOption="User Role"
                  icon={UserRoleIcon}
                  showLabel={true}
                />
                {(userRole === stafferRoleValue || userRole === managerRoleValue) && (
                  <CustomSelect
                    options={usersList}
                    selectedOption={mpp}
                    setSelectedOption={setMpp}
                    defaultOption="MPP"
                    icon={UserIcon}
                    showLabel={true}
                  />
                )}
                {userRole === mppRoleValue && (
                  <CustomSelect
                    options={ridings}
                    selectedOption={riding}
                    setSelectedOption={setRiding}
                    defaultOption="Riding"
                    icon={TopicIcon}
                    showLabel={true}
                    search={true}
                  />
                )}
                {userRole === mpRoleValue && (
                  <CustomSelect
                    options={federalDistricts}
                    selectedOption={federalDistrict}
                    setSelectedOption={setFederalDistrict}
                    defaultOption="Federal District"
                    icon={TopicIcon}
                    showLabel={true}
                    search={true}
                  />
                )}
                {userRole === councillorRoleValue && (
                  <CustomSelect
                    options={cities}
                    selectedOption={councillorCity}
                    setSelectedOption={setCouncillorCity}
                    defaultOption="City"
                    icon={TopicIcon}
                    showLabel={true}
                    search={true}
                  />
                )}
                {(userRole === stafferRoleValue ||
                  userRole === managerRoleValue ||
                  userRole === mppRoleValue ||
                  userRole === mpRoleValue ||
                  userRole === councillorRoleValue) && (
                  <Form.Check
                    type="switch"
                    id="demo"
                    label="Demo user"
                    checked={demoUser}
                    onChange={() => {
                      const newDemoUser = !demoUser;
                      setDemoUser(newDemoUser);
                      // Auto-check bypass2fa when demo user is enabled
                      if (newDemoUser) {
                        setBypass2fa(true);
                      }
                    }}
                  />
                )}
                <Form.Check
                  type="switch"
                  id="bypass2fa"
                  label="Bypass 2FA"
                  checked={bypass2fa}
                  onChange={() => setBypass2fa((prev) => !prev)}
                />
              </div>
              {userRole === adminRoleValue && (
                <div className="permissions">
                  <span>
                    Admin can add up to{' '}
                    <input type="number" value={usersLimit} onChange={(e) => setUsersLimit(e.target.value)} /> users
                    under his management
                  </span>
                  <Form.Check
                    type="switch"
                    id="topics-permission"
                    label="Admin can assign/delete Topics for a user"
                    checked={topicsPermission}
                    onChange={() => setTopicsPermission((prev) => !prev)}
                  />
                  <Form.Check
                    type="switch"
                    id="groups-permission"
                    label="Admin can assign/delete Groups for a user"
                    checked={groupsPermission}
                    onChange={() => setGroupsPermission((prev) => !prev)}
                  />
                  <Form.Check
                    type="switch"
                    id="trends-permission"
                    label="Admin can assign/delete Google Trend Topics for a user"
                    checked={trendsPermission}
                    onChange={() => setTrendsPermission((prev) => !prev)}
                  />
                  <Form.Check
                    type="switch"
                    id="district-permission"
                    label="Admin can assign/delete Electoral District (Elections + Maps) for a user"
                    checked={districtPermission}
                    onChange={() => setDistrictPermission((prev) => !prev)}
                  />
                  <Form.Check
                    type="switch"
                    id="trends-permission"
                    label="Admin can turn on/off MarketPulse for a user"
                    checked={mpPermission}
                    onChange={() => setMpPermission((prev) => !prev)}
                  />
                </div>
              )}
              <div className="submit">
                <FormButton
                  subLink={'Cancel'}
                  subLinkClick={onCloseHandler}
                  text={userData ? 'Save' : 'Create'}
                  onClick={userData ? handleSave : handleCreate}
                />
              </div>
            </>
          ) : (
            <Fader size={50} />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAdd;
